import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaSearch, FaTimes, FaChevronDown, FaCheck, FaExclamationTriangle } from "react-icons/fa";

import { formatProjectSeq } from "../../hooks/useClientsStatusMap";

// ===========================================================================
// NotesProjectPicker — searchable project selector for Project Notes.
//
// Purely presentational: it receives an already-built `projects` array
// (derived in Notes.js from the realtime `clientsById` map) and reports a
// clientDocId back through onChange. It owns no data, fetches nothing,
// and knows nothing about Firestore — so it can never become a second
// client/project source.
//
// The popover is rendered through a portal at fixed coordinates taken
// from the trigger's bounding rect. That's deliberate: the workspace card
// uses `overflow: hidden` to clip its rounded corners, which would
// otherwise cut off any absolutely-positioned dropdown inside it.
// ===========================================================================

const projectRef = (project) => `P-${formatProjectSeq(project.projectNumber)}`;

// Search runs across Client ID, client name, project type, and project
// number, so "CL-001", "ABC", "Residential" and "P-01" all resolve.
const matchesQuery = (project, query) => {
  const term = query.trim().toLowerCase();
  if (!term) return true;

  return (
    (project.clientId || "").toLowerCase().includes(term) ||
    (project.name || "").toLowerCase().includes(term) ||
    (project.projectType || "").toLowerCase().includes(term) ||
    projectRef(project).toLowerCase().includes(term) ||
    String(project.projectNumber || "").includes(term)
  );
};

function NotesProjectPicker({ projects = [], value, onChange, loading = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [activeIndex, setActiveIndex] = useState(0);
  const [rect, setRect] = useState(null);

  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const searchRef = useRef(null);
  const optionRefs = useRef([]);

  const selected = useMemo(
    () => projects.find((p) => p.clientDocId === value) || null,
    [projects, value],
  );

  // A note pointing at a client that no longer exists. The reference is
  // shown as unavailable and left completely untouched — never rewritten,
  // never cleared automatically.
  const isOrphan = !!value && !selected && !loading;

  // Project types come from the live project data, never a hard-coded
  // list, so a type added in Clients.js shows up here on its own.
  const availableTypes = useMemo(() => {
    const set = new Set();
    projects.forEach((p) => {
      if (p.projectType) set.add(p.projectType);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [projects]);

  const results = useMemo(() => {
    return projects
      .filter((p) => (typeFilter === "all" ? true : p.projectType === typeFilter))
      .filter((p) => matchesQuery(p, query));
  }, [projects, typeFilter, query]);

  // ---------------- Positioning ----------------
  const updateRect = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom + 6, left: r.left, width: r.width, bottom: r.top });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateRect();

    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [open, updateRect]);

  // ---------------- Open / close ----------------
  const openPicker = () => {
    if (loading) return;
    setQuery("");
    setActiveIndex(0);
    setOpen(true);
  };

  const closePicker = useCallback(() => {
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
  }, []);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  // Click outside closes.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      closePicker();
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open, closePicker]);

  // Escape closes from anywhere while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closePicker();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closePicker]);

  // Keep the highlighted option in view as the user arrows through.
  useEffect(() => {
    optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const choose = (project) => {
    onChange(project.clientDocId);
    closePicker();
    triggerRef.current?.focus();
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    onChange("");
  };

  // Enter selects the highlighted project. It never bubbles, so it can't
  // reach the note form and trigger an unintended submit/save.
  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      const project = results[activeIndex];
      if (project) choose(project);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (results.length ? (i + 1) % results.length : 0));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
    }
  };

  // ---------------- Trigger ----------------
  const renderTriggerBody = () => {
    if (loading) {
      return <span className="ntp-trigger-placeholder">Loading projects...</span>;
    }

    if (isOrphan) {
      return (
        <span className="ntp-trigger-orphan">
          <FaExclamationTriangle />
          Project unavailable
        </span>
      );
    }

    if (selected) {
      return (
        <span className="ntp-trigger-selected">
          <span className="ntp-trigger-name">{selected.name || "Unnamed client"}</span>
          <span className="ntp-trigger-sub">
            {selected.clientId || "No ID"} · {projectRef(selected)}
            {selected.projectType ? ` · ${selected.projectType}` : ""}
          </span>
        </span>
      );
    }

    return <span className="ntp-trigger-placeholder">Select project</span>;
  };

  return (
    <div className="ntp-root">
      <button
        type="button"
        ref={triggerRef}
        className={`ntp-trigger${open ? " is-open" : ""}${isOrphan ? " is-orphan" : ""}`}
        onClick={() => (open ? closePicker() : openPicker())}
        disabled={loading}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {renderTriggerBody()}

        <span className="ntp-trigger-actions">
          {value && !loading && (
            <span
              role="button"
              tabIndex={0}
              className="ntp-clear"
              title="Clear selected project"
              aria-label="Clear selected project"
              onClick={clearSelection}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  clearSelection(e);
                }
              }}
            >
              <FaTimes />
            </span>
          )}
          <FaChevronDown className="ntp-caret" />
        </span>
      </button>

      {open &&
        rect &&
        createPortal(
          <div
            ref={popoverRef}
            className="ntp-popover"
            style={{
              top: rect.top,
              left: rect.left,
              width: Math.max(rect.width, 320),
            }}
            role="listbox"
          >
            <div className="ntp-search">
              <FaSearch />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search by client, ID, project number or type"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={onSearchKeyDown}
              />
            </div>

            {availableTypes.length > 0 && (
              <div className="ntp-filters">
                <button
                  type="button"
                  className={`ntp-filter${typeFilter === "all" ? " is-active" : ""}`}
                  onClick={() => {
                    setTypeFilter("all");
                    setActiveIndex(0);
                  }}
                >
                  All types
                </button>
                {availableTypes.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`ntp-filter${typeFilter === type ? " is-active" : ""}`}
                    onClick={() => {
                      setTypeFilter(type);
                      setActiveIndex(0);
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}

            <div className="ntp-results">
              {projects.length === 0 ? (
                <div className="ntp-state">No projects available</div>
              ) : results.length === 0 ? (
                <div className="ntp-state">No matching projects found</div>
              ) : (
                results.map((project, idx) => {
                  const isSelected = project.clientDocId === value;
                  return (
                    <div
                      key={project.clientDocId}
                      ref={(el) => (optionRefs.current[idx] = el)}
                      role="option"
                      aria-selected={isSelected}
                      className={`ntp-option${idx === activeIndex ? " is-active" : ""}${
                        isSelected ? " is-selected" : ""
                      }`}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => choose(project)}
                    >
                      <div className="ntp-option-body">
                        <span className="ntp-option-top">
                          <span className="ntp-option-id">{project.clientId || "No ID"}</span>
                          <span className="ntp-option-name">
                            {project.name || "Unnamed client"}
                          </span>
                        </span>
                        <span className="ntp-option-sub">
                          {projectRef(project)}
                          {project.projectType ? ` · ${project.projectType}` : ""}
                          {project.status ? ` · ${project.status}` : ""}
                        </span>
                      </div>
                      {isSelected && <FaCheck className="ntp-option-check" />}
                    </div>
                  );
                })
              )}
            </div>

            <div className="ntp-footer">
              {results.length} of {projects.length} projects
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default NotesProjectPicker;